/**
 * This component creates an accordion with bookmarks populated as lists inside the dropdown.
 */
import React, { useContext } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import { Delete } from '@material-ui/icons';
import {
  ChapterListItem,
  IconButton,
  IconTooltip,
  NavigateToLocation,
} from 'components';
import { getMarkerTitle } from 'components/ChapterListItem/ChapterListItemHelpers';
import { UserContentItemModifiersContext } from 'contexts';
import { sortByIndexOfType } from 'utilities';

const useStyles = makeStyles((theme) => ({
  linkText: {
    display: 'flex',
    alignSelf: 'center',
    paddingLeft: theme.spacing(2),
  },
  titleContainer: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  bookmarkContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    color: theme.palette.greyBlue['700'],
    borderBottom: `solid 1px ${theme.palette.grey[300]}`,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: theme.spacing(0.5),
  },
  iconButton: {
    display: 'flex',
  },
  link: {
    display: 'flex',
    lineHeight: theme.typography.pxToRem(24), // 1.5rem
    padding: theme.typography.pxToRem(16), // 1rem
    paddingLeft: 0,
    paddingRight: 0,
    color: theme.palette.greyBlue['700'],
    fontWeight: theme.typography.fontWeightRegular,
    cursor: 'pointer',
    width: '100%',
  },
  hidden: {
    display: 'none',
  },
}));

const Bookmark = ({ bookmark, onClose }) => {
  const classes = useStyles();
  const { toggleBookmark } = useContext(UserContentItemModifiersContext);

  const { bookLocation, page } = bookmark;

  const marker = page?.marker;
  const pageLabel = page?.label;

  const markerTitle = marker ? getMarkerTitle(marker) : '';
  const title = `Page ${pageLabel}${markerTitle ? ` | ${markerTitle}` : ''}`;
  const ariaLabelContent = (
    <>
      Delete bookmark{' '}
      {markerTitle ? (
        <>
          in <span className="noTranslate">{markerTitle}</span>
        </>
      ) : (
        <></>
      )}
      {pageLabel ? (
        <>
          {' '}
          on page <span className="noTranslate">{pageLabel}</span>
        </>
      ) : (
        <></>
      )}
    </>
  );

  const styledTitle = (
    <Typography variant="body1" className={`${classes.linkText} noTranslate`}>
      <span>{title}</span>
    </Typography>
  );

  return (
    <li className={classes.bookmarkContainer}>
      <NavigateToLocation
        bookLocation={bookLocation}
        className={classes.link}
        id={bookmark.id}
        onClick={() => onClose(bookLocation)}
      >
        {styledTitle}
      </NavigateToLocation>

      <span className={classes.iconContainer}>
        <div
          className={classes.hidden}
          id={`delete-bookmark-label-${bookmark.id}`}
        >
          {ariaLabelContent}
        </div>
        <IconTooltip id="delete-bookmark" placement="top" title="Delete">
          <IconButton
            aria-labelledby={`delete-bookmark-label-${bookmark.id}`}
            className={classes.iconButton}
            onClick={() => toggleBookmark(bookLocation)}
          >
            <Delete />
          </IconButton>
        </IconTooltip>
      </span>
    </li>
  );
};

const BookmarkListItem = ({ children, topLevelMarker, onClose, ...props }) => {
  const classes = useStyles();

  const id = topLevelMarker?.index
    ? `bookmark-${topLevelMarker?.index}`
    : 'bookmark-no-chapter';

  // interpolation so the string doesn't get broken up in the DOM
  const chapterLabel = topLevelMarker?.label ?? 'No Chapter';
  const title = `${chapterLabel} (${children.length})`;

  const chapterTitle = (
    <Typography
      variant="body2"
      className={`${classes.titleContainer} noTranslate`}
      component="h3"
    >
      <span>{title}</span>
    </Typography>
  );

  return (
    <ChapterListItem
      id={id}
      isNavItem={false}
      title={chapterTitle}
      ariaLabelChapterTitle={chapterLabel}
      {...props}
    >
      {sortByIndexOfType(children, 'page').map((b) => (
        <React.Fragment key={`bookmark-${b.id}`}>
          <Bookmark bookmark={b} onClose={onClose} />
        </React.Fragment>
      ))}
    </ChapterListItem>
  );
};

export default BookmarkListItem;
